import React, { useCallback, useMemo } from 'react';
import { Button } from '@mui/material';
import PremiumCamIconMedium from 'components/Icons/PremiumCamIconMedium';
import Link from 'next/link';
import classNames from 'classnames';

import styles from './PremiumCamCTA.module.scss';

interface Copy {
  indicatorText?: string;
  headerText?: string;
  subheaderText?: string;
  ctaText?: string;
  learnMoreText?: string;
}

interface PremiumCamCTAProps {
  href?: string;
  onClickPaywall?: () => void;
  camStillUrl?: string;
  copy?: Copy;
  Icon?: React.FC;
}

const defaultCopy: Copy = {
  indicatorText: 'premium cam',
  headerText: 'This is a High Resolution, Premium Only Cam',
  subheaderText: 'Watch live conditions of this spot in full HD, ad-free',
  ctaText: 'Upgrade',
  learnMoreText: 'Learn more about Surfline Premium',
};

const PremiumCamCTA: React.FC<PremiumCamCTAProps> = ({
  href,
  onClickPaywall,
  camStillUrl,
  copy = {},
  Icon = PremiumCamIconMedium,
}) => {
  const onClickGoPremium = useCallback(() => {
    if (onClickPaywall) {
      onClickPaywall();
    }
    window.location.assign(href || '');
  }, [href, onClickPaywall]);

  const fullCopy = useMemo(
    () => ({
      ...defaultCopy,
      ...copy,
    }),
    [copy],
  );

  return (
    <div className={classNames(styles.premiumCamCtaContainer, 'quiver-premium-cam-cta__container')}>
      <div
        className="quiver-premium-cam-cta__camStill"
        style={{ backgroundImage: `url(${camStillUrl})` }}
        data-testid="quiver-premium-cam-cta__camStill"
      />
      <div className="quiver-premium-cam-cta">
        <div className="quiver-aspect-ratio-content quiver-premium-cam-cta__content__container">
          <div className="quiver-premium-cam-cta__content">
            <div className="quiver-premium-cam-cta__heading">{fullCopy.headerText}</div>
            <div className="quiver-premium-cam-cta__icon">{Icon && <Icon />}</div>
            <div className="quiver-premium-cam-cta__subheading">
              <span>{fullCopy.subheaderText}</span>
            </div>
            <div className="quiver-premium-cam-cta__links">
              <div className="quiver-premium-cam-cta__links__upgrade">
                <Button
                  onClick={onClickGoPremium}
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  size="medium"
                  color="dark"
                  variant="secondary"
                >
                  {fullCopy.ctaText}
                </Button>
              </div>
              <div className="quiver-premium-cam-cta__links__about">
                <Link href="/premium-benefits">{fullCopy.learnMoreText}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumCamCTA;
